"use client";
import { useState, useEffect, useRef } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AspectRatio } from "../ui/aspect-ratio";
import { motion, AnimatePresence } from "motion/react";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import { Reveal } from "../reveal";
import { useInView } from "@/hooks/useInView";
import { useAppSelector } from "@/store/hooks";
import { iconsMap } from "../icons";
import { PortableText } from "next-sanity";
import { components } from "../shared/portable-text";
import { SanityImage } from "../sanity-image";
import CarouselArrows from "./carousel-arrows";
import type { TabsSectionType } from "./tabSection.type";

export function Products({ data }: { data: TabsSectionType }) {
  const { tabItems } = data;
  const [autoAdvanceEnabled, setAutoAdvanceEnabled] = useState(true);
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [resetTimer, setResetTimer] = useState(0);
  const [tabsListRef, isInView] = useInView<HTMLDivElement>();
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const { isOverlayOpen } = useAppSelector((state) => state.overlay);
  const ref = useRef<HTMLDivElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoAdvanceEnabled && isInView && !isOverlayOpen && !isPaused) {
      interval = setInterval(() => {
        setCurrentTabIndex((prev) => (prev + 1) % tabItems.length);
      }, 3500);
    }

    return () => clearInterval(interval);
  }, [autoAdvanceEnabled, resetTimer, isInView, isOverlayOpen, isPaused, tabItems.length]);

  const scrollIntoView = () => {
    if (isInView) {
      ref.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  const handleNavigation = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAutoAdvanceEnabled(false);
    setTimeout(() => {
      setAutoAdvanceEnabled(true);
      setResetTimer((p) => p + 1);
    }, 10000);
  };

  return (
    <section className="relative max-w-screen-2xl mx-auto py-16 md:py-20 lg:py-24 w-full px-4 sm:px-6 md:px-8 bg-white">
      <CarouselArrows
        tabItems={tabItems}
        setCurrentTabIndex={setCurrentTabIndex}
        handleNavigation={handleNavigation}
      />
      <Reveal className="pb-3">
        <Tabs
          defaultValue={tabItems[0].name}
          value={tabItems[currentTabIndex].name}
          className="flex items-center justify-center flex-col gap-6 md:gap-12 lg:gap-16"
        >
          <ScrollArea className="relative w-[calc(100vw_-_32px)] md:w-full whitespace-nowrap mx-2 md:mx-0 px-2 md:px-0">
            {/* TABS */}
            <TabsList
              ref={tabsListRef}
              className="bg-white flex gap-1 w-max mx-auto h-16 items-center justify-center"
            >
              {tabItems.map((product, index) => (
                <TabsTrigger value={product.name} key={product.name} asChild>
                  <button
                    onClick={(e) => {
                      setCurrentTabIndex(index);
                      handleNavigation(e);
                    }}
                    className="relative py-2 px-2 text-xs gap-2 cursor-pointer data-[state=active]:text-secondary-500 group transition duration-500"
                  >
                    {currentTabIndex === index && (
                      <motion.div
                        layoutId="itadori"
                        onLayoutAnimationStart={scrollIntoView}
                        ref={ref}
                        className="absolute h-full w-full bg-secondary-50 rounded-full shadow-sm"
                      />
                    )}

                    {product.button.icon && (
                      <div className="text-base relative">{iconsMap[product.button.icon]}</div>
                    )}

                    <span className="relative">{product.button.textAfterIcon}</span>
                  </button>
                </TabsTrigger>
              ))}
            </TabsList>
            <ScrollBar orientation="horizontal" className="h-0.5" />
          </ScrollArea>

          <div
            ref={contentContainerRef}
            className="flex-1 relative"
            style={{ minHeight: "400px" }} // Fallback min-height
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setIsPaused(false)}
          >
            {tabItems.map((product) => (
              <TabsContent value={product.name} key={product.name} className="mx-2 md:mx-0">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={product.name}
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -30, opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="grid grid-cols-1 md:grid-cols-[1fr_auto] lg:grid-cols-[auto_auto] gap-3 md:gap-8 lg:gap-10 items-center justify-center w-full shadow-[0px_2.24px_5.59px_0px_rgba(0,_0,_0,_0.1)] md:shadow-none lg:shadow-none rounded-2xl md:rounded-none lg:rounded-none p-2 md:p-0 lg:p-0 bg-white md:bg-transparent lg:bg-transparent"
                  >
                    <div className="w-full max-w-[400px] md:max-w-none lg:max-w-[400px] flex flex-col px-4 pb-4 md:px-0 md:pb-0 lg:px-0 lg:pb-0">
                      <PortableText value={product.content} components={components} />
                    </div>
                    <div className="w-full md:w-[350px]  md:max-w-[450px] lg:w-[400px] xl:w-[500px] lg:max-w-none row-start-1 md:col-start-2 lg:col-start-2">
                      <AspectRatio ratio={549 / 463}>
                        <SanityImage
                          src={product.image}
                          alt="event"
                          fill
                          placeholder="blur"
                          blurDataURL={product.image?.asset?.metadata?.lqip || ""}
                          className="object-cover rounded-t-2xl md:rounded-2xl lg:rounded-b-2xl cursor-pointer"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        />
                      </AspectRatio>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </TabsContent>
            ))}
          </div>
        </Tabs>
      </Reveal>
    </section>
  );
}
